import * as React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: React.ReactNode;
  /** When this value changes, the boundary resets (e.g. on route change). */
  resetKey?: unknown;
}
interface State {
  error: Error | null;
}

/**
 * Catches render errors in the routed page area so one broken screen shows a
 * recoverable fallback instead of white-screening the whole console.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface for diagnostics; a real deployment would ship this to Sentry etc.
    console.error('CMS render error:', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <p className="text-lg font-semibold">This screen hit an error</p>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Something went wrong rendering this page. You can retry, or navigate elsewhere — the
              rest of the console is unaffected.
            </p>
          </div>
          <pre className="max-w-xl overflow-x-auto rounded-md bg-muted px-3 py-2 text-start text-xs text-muted-foreground">
            {this.state.error.message}
          </pre>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => this.setState({ error: null })}>
              <RotateCcw className="h-4 w-4" /> Try again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload console</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
